import type { FC } from 'react'
import type { ForkliftModel } from '@/types'

import { ArrowRight, Rotate3d } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CountUp } from '@/components/generals/count-up'
import { DragStage } from '@/components/landing/drag-stage'
import { hero } from '@/constants/content'
import { EXTENDED_RUNTIME_FACTOR } from '@/constants/lineup'

interface HeroProps {
    models: ForkliftModel[]
}

export const Hero: FC<HeroProps> = ({ models }) => {
    const maxCapacity = Math.max(...models.map(model => model.capacityKg))
    const maxLift = Math.max(...models.map(model => model.liftHeightM))
    const maxRuntime = Math.max(...models.map(model => Math.round(model.runtimeHours * EXTENDED_RUNTIME_FACTOR)))

    const stats = [
        { value: maxCapacity, decimals: 0, suffix: ' kg', label: "Max capacity" },
        { value: maxLift, decimals: 1, suffix: ' m', label: "Max lift height" },
        { value: maxRuntime, decimals: 0, suffix: ' h', label: "Runtime per charge" },
        { value: 1, decimals: 0, suffix: ' h', label: "Fast charge to 80%" }
    ]

    const [first, ...rest] = hero.title.split(', ')

    return (
        <section
            id='hero'
            data-chapter='0'
            aria-labelledby='hero-title'
            className='relative flex min-h-[100svh] flex-col'
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-linear-to-t from-background via-background/75 via-45% to-transparent to-70% [opacity:var(--fade,1)] landscape:bg-linear-to-r landscape:from-background/90 landscape:via-background/50 landscape:via-35% landscape:to-transparent landscape:to-60%'
            />

            <DragStage className='absolute inset-x-0 top-16 h-[38svh] landscape:left-auto landscape:right-0 landscape:top-[14%] landscape:h-[70%] landscape:w-[52%]' />

            <div className='pointer-events-none relative mx-auto flex w-full max-w-7xl flex-1 items-end px-5 pb-10 pt-[46svh] sm:px-8 landscape:items-center landscape:pb-36 landscape:pt-28 short:landscape:pb-24'>
                <div className='chapter-content pointer-events-auto max-w-[40rem] landscape:max-w-[36rem]'>
                    <p className='inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-white/80'>
                        <span className='relative flex size-2'>
                            <span className='absolute inset-0 animate-ping-soft rounded-full bg-brand' />
                            <span className='relative size-2 rounded-full bg-brand' />
                        </span>
                        {hero.eyebrow}
                    </p>

                    <h1
                        id='hero-title'
                        className='mt-6 font-heading font-expanded text-[clamp(2.1rem,9.4vw,3rem)] font-extrabold leading-[0.98] tracking-tight text-white landscape:text-[clamp(2.6rem,4.6vw,4.9rem)] short:landscape:text-[clamp(2rem,4vw,3rem)]'
                    >
                        {first},
                        <br />
                        <span className='text-brand'>
                            {rest.join(', ')}
                        </span>
                    </h1>

                    <p className='mt-6 max-w-[34rem] text-base leading-relaxed text-white/70 sm:text-lg short:landscape:mt-4 short:landscape:text-base'>
                        {hero.body}
                    </p>

                    <div className='mt-8 flex flex-wrap gap-3 short:landscape:mt-5'>
                        <a
                            href='#configure'
                            className='group inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-semibold text-brand-foreground transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-12px_var(--brand)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:text-base'
                        >
                            Configure yours
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </a>
                        <a
                            href='#quote'
                            className='inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/35 hover:bg-white/[0.07] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:text-base'
                        >
                            Get a quote
                        </a>
                    </div>

                    <dl className='mt-9 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 landscape:hidden'>
                        {stats.slice(0, 3).map(stat => (
                            <div key={stat.label} className='flex flex-col-reverse gap-1'>
                                <dt className='text-[11px] leading-snug text-white/55'>
                                    {stat.label}
                                </dt>
                                <dd className='font-heading font-expanded text-lg font-bold text-white'>
                                    <CountUp value={stat.value} decimals={stat.decimals} suffix={stat.suffix} />
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>

            <div className='pointer-events-none absolute inset-x-0 bottom-0 hidden landscape:block'>
                <div className='chapter-content mx-auto flex max-w-7xl items-end justify-between gap-8 px-5 pb-8 sm:px-8'>
                    <dl className='flex divide-x divide-white/10'>
                        {stats.map((stat, index) => (
                            <div key={stat.label} className={cn('flex flex-col-reverse gap-1 pr-8', index > 0 && 'pl-8')}>
                                <dt className='text-xs text-white/55'>
                                    {stat.label}
                                </dt>
                                <dd className='font-heading font-expanded text-2xl font-bold text-white short:text-xl'>
                                    <CountUp value={stat.value} decimals={stat.decimals} suffix={stat.suffix} />
                                </dd>
                            </div>
                        ))}
                    </dl>

                    <div className='flex flex-col items-end gap-5'>
                        <p className='inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3.5 py-1.5 text-xs text-white/65'>
                            <Rotate3d className='size-4 text-brand' />
                            Drag the truck to rotate it
                        </p>
                        <div className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white/45'>
                            Scroll to explore
                            <span className='relative h-10 w-px overflow-hidden bg-white/15'>
                                <span className='absolute inset-x-0 top-0 h-1/2 animate-scroll-hint bg-brand' />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
